// Libraries
import { Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { TextWithTooltip } from '@atoms/index';
import { useDebouncedState } from '@hooks/index';
import { CustomNumberInput, CustomSlider } from './components';
// Utils
import { formatNumericString, isValidNumber, roundToStep } from '@helpers/index';
// Types
import { SliderInputProps } from './SliderInput.types';

/**
 * A combined input component featuring a slider and a number input.
 *
 * @component
 * @param {SliderInputProps} props - The props for the SliderInput component.
 * @param {ReactNode} props.children - React children elements.
 * @param {string} props.currency - String used to add a currency symbol.
 * @param {'end'|'start'} props.currencyPosition - Location of the currency symbol.
 * @param {string} props.error - Current error message.
 * @param {number} props.initialValue - The initial value of the slider.
 * @param {boolean} [props.isDisabled=false] - Determines if the slider and number input are disabled.
 * @param {boolean} [props.isLoading=false] - Indicates if the component is in a loading state.
 * @param {string} props.label - Label displayed next to the slider.
 * @param {number} props.max - Maximum value for the slider and number input.
 * @param {number} props.maxW - Max width of the input element.
 * @param {number} props.min - Minimum value for the slider and number input.
 * @param {string} props.name - Name of the input field.
 * @param {Function} props.onChange - Callback fired when the value changes.
 * @param {number} [props.precision=0] - Number of decimal places for the number input.
 * @param {Function} props.setError - A function to set the error state.
 * @param {number} [props.step=1] - Step value for the slider and number input.
 * @param {string} props.tooltipText - Tooltip text to be displayed alongside the label.
 * @param {number} props.value - Current value of the slider and number input.
 *
 * @returns {ReactElement} The SliderInput component.
 */
export const SliderInput = ({
  children,
  currency,
  currencyPosition,
  error,
  initialValue,
  isDisabled = false,
  isLoading = false,
  label,
  max,
  maxW,
  min,
  onChange,
  precision = 0,
  step = 1,
  tooltipText,
  value,
  maxLength,
}: SliderInputProps) => {
  const [internalValue, setInternalValue] = useDebouncedState(value, 100, onChange);
  const sliderInitialValue = isValidNumber(initialValue) ? Number(initialValue) : undefined;
  const sliderValue = isValidNumber(internalValue) ? Number(internalValue) : min;

  return (
    <Flex direction='column' w='full' minW={60} gap={1}>
      <Flex gap={3}>
        <TextWithTooltip fontSize='m' color='text.base' tooltipText={tooltipText} whiteSpace='nowrap'>
          {label}
        </TextWithTooltip>
        {children}
      </Flex>
      <Flex gap={5}>
        <Skeleton isLoaded={!isLoading} flex='1' pb={3.5}>
          <CustomSlider
            max={roundToStep(max, step)}
            min={roundToStep(min, step)}
            step={step}
            isDisabled={isDisabled}
            value={sliderValue}
            initialValue={sliderInitialValue}
            setValue={setInternalValue}
          />
        </Skeleton>
        <Skeleton isLoaded={!isLoading}>
          <CustomNumberInput
            currency={currency}
            currencyPosition={currencyPosition}
            min={min}
            max={max}
            step={step}
            precision={precision}
            isDisabled={isDisabled}
            isInvalid={!!error}
            value={formatNumericString(internalValue, precision)}
            setValue={setInternalValue}
            maxW={maxW}
            maxLength={maxLength}
          />
          {!!error && (
            <Text h={3.5} mt={1} color='red'>
              {error}
            </Text>
          )}
        </Skeleton>
      </Flex>
    </Flex>
  );
};
